import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Star } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getPublicMentorReviews, getMentorRatingStats, type MentorReview } from "@/services/mentorReviewService";
import { format, parseISO } from "date-fns";
import { ptBR } from 'date-fns/locale';
import { Skeleton } from "@/components/ui/skeleton";

interface MentorReviewsSectionProps {
  mentorId: string;
}

export const MentorReviewsSection: React.FC<MentorReviewsSectionProps> = ({ mentorId }) => {
  const [reviews, setReviews] = useState<MentorReview[]>([]);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (mentorId) {
      fetchReviews();
    }
  }, [mentorId]);

  const fetchReviews = async () => {
    try {
      setIsLoading(true);
      
      const [reviewsData, statsData] = await Promise.all([
        getPublicMentorReviews(mentorId),
        getMentorRatingStats(mentorId)
      ]);

      setReviews(reviewsData);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching public reviews:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${
              i < rating
                ? "text-yellow-400 fill-yellow-400"
                : "text-gray-600"
            }`}
          />
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className="bg-[#131922] border-[#222A35] mt-6">
        <CardHeader>
          <CardTitle className="text-white">Últimas Avaliações</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-[#1A2231] p-4 rounded-lg">
                <div className="flex items-center mb-4">
                  <Skeleton className="h-10 w-10 rounded-full mr-4" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="h-16 w-full" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <Card className="bg-[#131922] border-[#222A35] mt-6 mb-8">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white">Últimas Avaliações</CardTitle>
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
            <span className="text-white font-bold">{stats.averageRating.toFixed(1)}</span>
            <span className="text-gray-400">({stats.totalReviews} avaliações)</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="bg-[#1A2231] p-4 rounded-lg">
              <div className="flex items-center mb-4">
                <Avatar className="h-10 w-10 mr-4">
                  <AvatarImage 
                    src={review.player_profile?.image_url} 
                    alt={review.player_profile?.nickname} 
                  />
                  <AvatarFallback>
                    {review.player_profile?.nickname?.charAt(0) || "J"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-white font-medium">
                      {review.player_profile?.nickname || "Jogador"}
                    </p>
                    {renderStars(review.rating)}
                  </div>
                  <p className="text-gray-400 text-sm">
                    {format(parseISO(review.created_at), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </p>
                </div>
              </div>
              
              {review.session?.mentorship_offerings?.title && (
                <p className="text-sm text-[#00B6F0] mb-2">
                  Mentoria: {review.session.mentorship_offerings.title}
                </p>
              )}
              
              <p className="text-gray-300">{review.comment}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}; 